import * as React from "react";
import { useRecordContext } from "react-admin";
import { Link } from "react-router-dom";
import { Avatar, Box, Card, CardContent, Chip, Stack, Typography, alpha } from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import { StatusChip } from "./ui";
import { ACCOUNT_STATUS, BOOKING_STATUS, BOOKING_TYPE, PAYMENT_STATUS, FORFAIT_STATUS } from "../enums";
import { fmtDate, fmtDateTime, fmtMoney, fullName, initialsOf } from "../utils/format";

const get = (row, path) => String(path).split(".").reduce((v, p) => (v == null ? undefined : v[p]), row);

const pick = (row, ...keys) => {
  for (const k of keys) {
    const v = get(row, k);
    if (v != null && v !== "") return v;
  }
  return undefined;
};

const MetaItem = ({ icon: Icon, children, href }) => (
  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: "text.secondary", minWidth: 0 }}>
    <Icon sx={{ fontSize: 17 }} />
    {href ? (
      <Box component="a" href={href} sx={{ color: "inherit", textDecoration: "none", fontSize: 14, "&:hover": { color: "primary.main" }, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {children}
      </Box>
    ) : (
      <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{children}</Typography>
    )}
  </Stack>
);

// En-tête d'une fiche utilisateur (patient / pro) : avatar, nom, statut,
// coordonnées et actions.
export const ProfileHeader = ({ kind = "patient", chips, actions, subtitle }) => {
  const r = useRecordContext();
  if (!r) return null;
  const photo = pick(r, "photo.url", "urlPhoto");
  const phone = pick(r, "phoneNumber", "phone", "address.phone");
  // Ville : adresse du profil, sinon 1er cabinet (fiche pro).
  const addr = pick(r, "address.city")
    ? r.address
    : pick(r, "professionalCard.professionalAddresses.0.city")
      ? r.professionalCard.professionalAddresses[0]
      : pick(r, "professionalAddresses.0.city")
        ? r.professionalAddresses[0]
        : r.address;
  const city = [addr?.postalCode, addr?.city].filter(Boolean).join(" ");
  const isPro = kind === "pro";
  return (
    <Card sx={{ mb: 2 }}>
      <Box
        sx={(t) => ({
          height: 64,
          background: isPro
            ? `linear-gradient(120deg, ${alpha(t.palette.primary.main, 0.16)}, ${alpha(t.palette.primary.main, 0.04)})`
            : `linear-gradient(120deg, ${alpha(t.palette.info.main, 0.14)}, ${alpha(t.palette.info.main, 0.03)})`,
        })}
      />
      <CardContent sx={{ pt: 0, mt: -4.5 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "flex-end" }} justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="flex-end" sx={{ minWidth: 0 }}>
            <Avatar
              src={photo || undefined}
              sx={(t) => ({
                width: 84,
                height: 84,
                fontSize: 28,
                fontWeight: 700,
                fontFamily: "Sora, sans-serif",
                border: `4px solid ${t.palette.background.paper}`,
                bgcolor: isPro ? "primary.main" : "info.main",
              })}
            >
              {initialsOf(r.firstName, r.lastName)}
            </Avatar>
            <Box sx={{ minWidth: 0, pb: 0.5 }}>
              <Typography variant="h5" component="h1" sx={{ lineHeight: 1.2 }}>
                {fullName(r.firstName, r.lastName, r.email || "Sans nom")}
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={isPro ? "Professionnel" : "Patient"} color={isPro ? "primary" : "info"} />
                <StatusChip value={r.accountStatus} enumObj={ACCOUNT_STATUS} />
                {chips}
              </Stack>
            </Box>
          </Stack>
          {actions && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ pb: 0.5 }}>
              {actions}
            </Stack>
          )}
        </Stack>
        <Box
          sx={{
            mt: 2,
            display: "grid",
            gap: 1.25,
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0,1fr))", lg: "repeat(4, minmax(0,1fr))" },
          }}
        >
          <MetaItem icon={MailOutlineIcon} href={r.email ? `mailto:${r.email}` : undefined}>{r.email || "—"}</MetaItem>
          <MetaItem icon={PhoneOutlinedIcon} href={phone ? `tel:${phone}` : undefined}>{phone || "—"}</MetaItem>
          <MetaItem icon={PlaceOutlinedIcon}>{city || "—"}</MetaItem>
          <MetaItem icon={EventOutlinedIcon}>Inscrit le {fmtDate(r.creationDate || r.createdAt)}</MetaItem>
        </Box>
        {subtitle}
      </CardContent>
    </Card>
  );
};

const linkSx = { color: "primary.main", fontWeight: 600, textDecoration: "none", "&:hover": { textDecoration: "underline" } };

export const RecordLink = ({ to, children }) => (
  <Box component={Link} to={to} sx={linkSx} onClick={(e) => e.stopPropagation()}>
    {children}
  </Box>
);

// Colonnes RemoteList pour les rendez-vous d'un patient / d'un pro.
export const bookingColumns = (other = "pro") => [
  {
    key: "start",
    label: "Date",
    render: (b) => (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>{fmtDateTime(pick(b, "slot.startTime", "startAt", "startTime"))}</Typography>
        <Typography variant="caption" color="text.secondary">
          {(() => {
            const d = pick(b, "slot.duration");
            if (d) return `${d} min`;
            const s = pick(b, "slot.startTime");
            const e = pick(b, "slot.endTime");
            return s && e ? `${Math.round((e - s) / 60000)} min` : "";
          })()}
        </Typography>
      </Box>
    ),
  },
  other === "pro"
    ? {
        key: "pro",
        label: "Professionnel",
        render: (b) => {
          const name = fullName(b.firstNamePro, b.lastNamePro);
          const id = pick(b, "professionalId", "proId");
          return id ? <RecordLink to={`/professionals/${encodeURIComponent(id)}/show`}>{name || "Voir la fiche"}</RecordLink> : name || "—";
        },
      }
    : {
        key: "patient",
        label: "Patient",
        render: (b) => {
          const name = fullName(b.firstNamePatient, b.lastNamePatient);
          const id = pick(b, "patientId");
          return id ? <RecordLink to={`/patients/${encodeURIComponent(id)}/show`}>{name || "Voir la fiche"}</RecordLink> : name || "—";
        },
      },
  { key: "spec", label: "Spécialité", render: (b) => pick(b, "nameSpeciality") || "—" },
  { key: "type", label: "Mode", render: (b) => <StatusChip value={pick(b, "bookingType")} enumObj={BOOKING_TYPE} /> },
  { key: "status", label: "Statut", render: (b) => <StatusChip value={pick(b, "status")} enumObj={BOOKING_STATUS} /> },
  { key: "price", label: "Prix", align: "right", render: (b) => fmtMoney(pick(b, "price", "fixedPrice")) },
  {
    key: "open",
    label: "",
    align: "right",
    render: (b) => (b?.id ? <RecordLink to={`/bookings/${encodeURIComponent(b.id)}/show`}>Détails →</RecordLink> : null),
  },
];

export const forfaitColumns = [
  { key: "amount", label: "Montant", render: (f) => fmtMoney(pick(f, "amount", "price")) },
  { key: "sessions", label: "Séances", render: (f) => pick(f, "totalSessions", "sessions") ?? "—" },
  { key: "minutes", label: "Minutes", render: (f) => pick(f, "totalMinutes", "minutes") ?? "—" },
  { key: "status", label: "Statut", render: (f) => <StatusChip value={pick(f, "status")} enumObj={FORFAIT_STATUS} /> },
];

export const paymentStatusChip = (v) => <StatusChip value={v} enumObj={PAYMENT_STATUS} />;

export const Stars = ({ value }) => {
  const n = Math.round(Number(value) || 0);
  return (
    <Box component="span" sx={{ color: "warning.main", letterSpacing: 1, whiteSpace: "nowrap" }} aria-label={`${n} sur 5`}>
      {"★".repeat(n)}
      <Box component="span" sx={{ color: "divider" }}>{"★".repeat(Math.max(0, 5 - n))}</Box>
    </Box>
  );
};

export { pick };
